import React, { useEffect, useMemo, useState } from 'react';
import { FileJson } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import BillingPeriodCell from '../BillingPeriodCell';
import EOEPreviewPopup from '../EOEPreviewPopup';
import { BillingDataTable } from '../BillingDataTable';
import { BillingActionsCell } from '../BillingActionsCell';
import { BillingSelectionActionBar } from '../BillingSelectionActionBar';
import { draftBillingCycleNoteContext } from '../billingCycleNoteContext';
import { PatientCell } from '../PatientCell';
import { InactiveEpisodeBadge } from '../InactiveEpisodeBadge';
import { InsuranceCell } from '../InsuranceCell';
import { NoRemittancesNotice } from '../NoRemittances';
import {
  getDraftBillingCycles,
  setBillingCycleStatusPendingApproval,
} from '../../../services/api';
import type { GetBillingCyclesResponse } from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { showCreateClaimFailures } from '../showCreateClaimFailures';
import {
  DRAFT_VOID_NOTES_WARNING,
  VoidClaimsConfirmDialog,
  type VoidCopyOptions,
} from '../VoidClaimsButton';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useStore } from '../../../store';
import { PermissionSlug } from '../../../constants/featureFlags';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import {
  useClaimReadiness,
  type DraftReadinessScope,
} from '../ClaimReadinessProvider';
import { ClaimReadinessCell } from '../ClaimReadinessCell';

interface EOEReadyTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEReadyTab: React.FC<EOEReadyTabProps> = ({ onCountsInvalidated }) => {
  const [draftPopupCycle, setDraftPopupCycle] =
    useState<GetBillingCyclesResponse | null>(null);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);

  const query = useBillingTableQuery('ready', {
    sortKeyByColumnId: { billing_period: 'start_date' },
  });
  const {
    data,
    isLoading,
    isRefreshing,
    isSubmitting,
    selectedIds,
    selectedItems,
    total,
    totalPages,
    clearSelection,
    removeSelectedId,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    voidSelected,
    loadData,
    showError,
    queryKey,
    isPlaceholderData,
  } = useBillingCycles<GetBillingCyclesResponse, { ready_for_review: boolean }>(
    {
      tabId: 'ready',
      fetchFn: getDraftBillingCycles,
      fetchPayload: { ready_for_review: true, ...query.toParams() },
      errorMessage: 'Could not fetch data for the "Ready" tab.',
      getId: item => item.billing_cycle_id,
      getName: item => item.patient_name,
      getMrn: item => item.patient_mrn,
    }
  );

  // Register this tab's current list identity so the readiness provider serves
  // its details against the list actually on screen, and hand the same scope to
  // every cell and to the draft preview so none can read another list.
  const scope = useMemo<DraftReadinessScope>(
    () => ({ tabId: 'ready', queryKey, isPlaceholderData }),
    [queryKey, isPlaceholderData]
  );
  const { registerList } = useClaimReadiness();
  useEffect(() => {
    registerList(scope);
  }, [registerList, scope]);

  const addToast = useStore(state => state.addToast);
  const canEditInsurance = useStore(state =>
    state.hasPermission(PermissionSlug.AGENCY_INSURANCE_EDIT)
  );
  const canSubmitClaims = useStore(state =>
    state.hasPermission(PermissionSlug.CLAIMS_SUBMIT)
  );

  const handleCreate = () => {
    void mutateSelected(
      id =>
        setBillingCycleStatusPendingApproval({
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles failed to update',
      'The following billing cycles could not be set to Pending Approval',
      onCountsInvalidated,
      failures =>
        showCreateClaimFailures(
          failures,
          (title, message, linkAction) =>
            linkAction
              ? // Longer than the default, so there is time to use the link.
                addToast({
                  title,
                  description: message,
                  variant: 'destructive',
                  linkAction,
                  duration: 15000,
                })
              : showError(title, message),
          { canEditInsurance }
        )
    );
  };

  // EMR-2423: a draft has nothing worked, so the void carries nothing forward.
  const handleVoid = (options: VoidCopyOptions) => {
    setVoidConfirmOpen(false);
    void voidSelected(options, onCountsInvalidated);
  };

  const columns = useMemo<ColumnDef<GetBillingCyclesResponse>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <div className='flex flex-col items-start gap-1'>
            <PatientCell
              name={row.original.patient_name}
              mrn={row.original.patient_mrn}
              patientId={row.original.patient_id}
            />
            {row.original.episode_status === 'cancelled' && (
              <InactiveEpisodeBadge className='self-start' />
            )}
          </div>
        ),
      },
      {
        id: 'clinic_name',
        accessorKey: 'clinic_name',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
            receivesRemittances={row.original.receives_remittances}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.start_date,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.start_date)}
            endDate={formatDateToMMDDYYYY(row.original.end_date)}
          />
        ),
      },
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.end_date) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.end_date}
            createdAt={row.original.created_at}
          />
        ),
      },
      {
        id: 'readiness',
        header: 'Readiness',
        enableSorting: false,
        cell: ({ row }) => (
          <ClaimReadinessCell entry={row.original} scope={scope} />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            showDownload={false}
            noteProps={{
              ...draftBillingCycleNoteContext(row.original, true),
              patientName: row.original.patient_name,
            }}
            leading={
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      onClick={() => setDraftPopupCycle(row.original)}
                      className='text-green-600 hover:text-green-700'
                      aria-label='Preview claim'
                    >
                      <FileJson className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='bottom' className='text-xs'>
                    Preview claim
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            }
          />
        ),
      },
    ],
    [scope]
  );

  return (
    <div className='flex flex-col gap-4'>
      <NoRemittancesNotice rows={data} />
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        selection={{
          selectedIds,
          onToggleId: handleToggleId,
          onToggleAllVisible: handleToggleAll,
        }}
      />
      <VoidClaimsConfirmDialog
        open={voidConfirmOpen}
        selectedCount={selectedIds.size}
        postSend={false}
        draft
        appendedMessage={DRAFT_VOID_NOTES_WARNING}
        onConfirm={handleVoid}
        onCancel={() => setVoidConfirmOpen(false)}
      />
      <BillingSelectionActionBar
        selectedItems={selectedItems}
        onClear={clearSelection}
        onRemoveSelectedId={removeSelectedId}
        actions={[
          ...(canSubmitClaims
            ? [
                {
                  key: 'create-claim',
                  label: 'Create Claim',
                  disabledReason: () =>
                    isSubmitting || isLoading
                      ? 'Action in progress'
                      : undefined,
                  run: handleCreate,
                },
              ]
            : []),
          {
            key: 'void',
            label: 'Void',
            destructive: true,
            disabledReason: () =>
              isSubmitting || isLoading ? 'Action in progress' : undefined,
            run: () => setVoidConfirmOpen(true),
          },
        ]}
      />

      <EOEPreviewPopup
        billingCycle={draftPopupCycle}
        readinessScope={scope}
        onOpenChange={open => !open && setDraftPopupCycle(null)}
        onBillingDataChanged={() => {
          void loadData();
          void onCountsInvalidated?.();
        }}
      />
    </div>
  );
};

export default EOEReadyTab;
